import React, { useEffect, useState, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../../../../../components/Layout";
import Container from "../../../../../../../../components/Container";
import Flex from "../../../../../../../../components/Flex";
import Content from "../../../../../../../../components/Content";
import Button from "../../../../../../../../components/Button";
import ButtonList from "../../../../../../../../components/ButtonList";
import Breadcrumbs from "../../../../../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../../../../../components/MessageProvider";
import { useFetch } from "../../../../../../../../components/useFetch";
import Label from "../../../../../../../../components/Label";
import DropDownList from "../../../../../../../../components/DropDownList";
import Spinner from "../../../../../../../../components/Loader/Spinner";
import TextBox from "../../../../../../../../components/TextBox";
import errorMessageHandler from "../../../../../../errorMessageHandler";

import styles from "./AddEditElementUseScore.module.scss";

const AddEditElementUseScore = () => {
  const elementUseMap = {
    name: [
      "usual",
      "official",
      "temp",
      "nickname",
      "anonymous",
      "old",
      "maiden"
    ],
    address: ["home", "work", "temp", "old", "billing"],
    telecom: ["phone", "fax", "email", "pager", "url", "sms", "other"]
  };

  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const organizationApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const sourcesApiUrl = `${clientConfigApiDomain}/sources`;
  const elementTrustScoreApiUrl = `${clientConfigApiDomain}/element_trust_scores`;
  const elementUseScoreApiUrl = `${clientConfigApiDomain}/element_use_scores`;

  const {
    id,
    clientId,
    sourceId,
    elementTrustId,
    elementUseTrustScoreId
  } = useParams();

  const history = useHistory();
  const toast = useMessageToast();

  const [orgData, setOrgData] = useState({});
  const [clientData, setClientData] = useState({});
  const [sourceData, setSourceData] = useState({});
  const [elementTrustData, setElementTrustData] = useState({});
  const [elementUseScoreData, setElementUseScoreData] = useState([]);
  const [elementUseList, setElementUseList] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [
    { hasLoaded: addEditHasLoaded, hasError: addEditHasError, error },
    addEditFetch
  ] = useFetch();

  const fetchData = useCallback(async () => {
    const orgResponse = await fetch(`${organizationApiUrl}/${id}/`);
    const orgData = await orgResponse.json();
    setOrgData(orgData);

    const clientResponse = await fetch(`${clientsApiUrl}/${clientId}/`);
    const clientData = await clientResponse.json();
    setClientData(clientData);

    const sourceResponse = await fetch(`${sourcesApiUrl}/${sourceId}/`);
    const sourceData = await sourceResponse.json();
    setSourceData(sourceData);

    const elementTrustScoreResponse = await fetch(
      `${elementTrustScoreApiUrl}/${elementTrustId}/`
    );
    const elementTrustScoreData = await elementTrustScoreResponse.json();
    setElementTrustData(elementTrustScoreData);

    if (elementUseTrustScoreId !== undefined) {
      for (let eus of elementTrustScoreData.element_use_scores) {
        if (eus.id === Number(elementUseTrustScoreId)) {
          setElementUseScoreData(eus);
        }
      }
    }
    const existingUseList = elementTrustScoreData.element_use_scores.map(
      e => e.use
    );
    console.log(
      elementUseMap[elementTrustScoreData.element].filter(
        use => !existingUseList.includes(use)
      )
    );
    setElementUseList(
      elementUseMap[elementTrustScoreData.element].filter(
        use => !existingUseList.includes(use)
      )
    );

    setHasLoaded(true);
  }, [
    id,
    clientId,
    sourceId,
    elementTrustId,
    organizationApiUrl,
    clientsApiUrl,
    sourcesApiUrl,
    elementTrustScoreApiUrl,
    elementUseMap,
    elementUseTrustScoreId
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchData();
    } else {
      setHasLoaded(true);
    }
  }, [hasLoaded, fetchData]);

  useEffect(() => {
    if (elementUseTrustScoreId === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`Element use score added successfully.`);
        history.push(
          `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/${elementTrustId}`
        );
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to add element use score.`);
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`Element use score updated successfully.`);
        history.push(
          `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/${elementTrustId}`
        );
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to update this element use score.`);
      }
    }
  }, [
    id,
    clientId,
    sourceId,
    elementTrustId,
    addEditHasError,
    addEditHasLoaded,
    toast,
    history,
    error,
    elementUseTrustScoreId
  ]);

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          {!hasLoaded ? <Spinner /> : null}
          {hasLoaded ? (
            <Breadcrumbs>
              <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
              <Breadcrumbs.Item
                render={() => <Link to="/admin">Administration</Link>}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link to="/admin/organizations">Organizations</Link>
                )}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link to={`/admin/organizations/${orgData.id}`}>
                    Organization: {orgData.name}
                  </Link>
                )}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link
                    to={`/admin/organizations/${orgData.id}/accounts/${clientData.id}`}
                  >
                    Account: {clientData.name}
                  </Link>
                )}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link
                    to={`/admin/organizations/${orgData.id}/accounts/${clientData.id}/sources/${sourceData.id}`}
                  >
                    Source: {sourceData.name}
                  </Link>
                )}
              />
              <Breadcrumbs.Item
                render={() => (
                  <Link
                    to={`/admin/organizations/${orgData.id}/accounts/${clientData.id}/sources/${sourceData.id}/element_trust/${elementTrustData.id}`}
                  >
                    Element: {elementTrustData.element}
                  </Link>
                )}
              />
              {elementUseTrustScoreId !== undefined ? (
                <Breadcrumbs.Item
                  last
                  render={() => "Edit Element Use Trust Score"}
                />
              ) : (
                <Breadcrumbs.Item
                  last
                  render={() => "Add Element Use Trust Score"}
                />
              )}
            </Breadcrumbs>
          ) : null}
          {hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {elementUseTrustScoreId !== undefined ? (
                  <h1>
                    Edit Element Use Trust Score: {elementUseScoreData.use}
                  </h1>
                ) : (
                  <h1>Add Element Use Trust Score</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}></div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      elementUseName: elementUseScoreData.use,
                      elementUseScore: elementUseScoreData.score,
                      elementTrustScore: elementTrustData.element_trust_score
                    }}
                    enableReinitialize={true}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        use: values.elementUseName,
                        score: values.elementUseScore,
                        element_trust_score: elementTrustData.url
                      };
                      let method = "";
                      let url = "";
                      if (elementUseTrustScoreId) {
                        data["id"] = elementUseTrustScoreId;
                        method = "put";
                        url = `${elementUseScoreApiUrl}/${elementUseTrustScoreId}/`;
                      } else {
                        method = "post";
                        url = `${elementUseScoreApiUrl}/`;
                      }
                      addEditFetch({ method, url, data });
                      setSubmitting(false);
                    }}
                    validationSchema={Yup.object().shape({
                      elementUseName: Yup.string()
                        .trim()
                        .required("The element use name is required"),
                      elementUseScore: Yup.string()
                        .trim()
                        .required("The element use score is required")
                        .matches(/^[0-9.]*$/, "invalid number format")
                    })}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit}>
                          <div>
                            {elementUseTrustScoreId === undefined ? (
                              <DropDownList
                                errorText={errors.elementUseName}
                                id="elementUseName"
                                label={
                                  <Label
                                    error={
                                      touched.elementUseName &&
                                      errors.elementUseName
                                    }
                                    htmlFor="elementUseName"
                                  >
                                    <b>Use Name</b>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.client}
                                width="25%"
                              >
                                <DropDownList.Item value="">
                                  Choose an element name...
                                </DropDownList.Item>
                                {elementUseList.map(use => (
                                  <DropDownList.Item key={use} value={use}>
                                    {use}
                                  </DropDownList.Item>
                                ))}
                              </DropDownList>
                            ) : null}
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.elementUseScore
                                  ? errors.elementUseScore
                                  : null
                              }
                              id="elementUseScore"
                              label={
                                <Label
                                  error={
                                    touched.elementUseScore &&
                                    errors.elementUseScore
                                  }
                                  htmlFor="elementUseScore"
                                >
                                  <b>Score</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.elementUseScore}
                              width="25%"
                            />
                          </div>
                          <ButtonList>
                            <Button primary type="submit">
                              {elementUseTrustScoreId !== undefined
                                ? "Save Changes"
                                : "Save"}
                            </Button>
                            <Button
                              onClick={() => {
                                history.push(
                                  `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/${elementTrustId}`
                                );
                              }}
                            >
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditElementUseScore;
